import {useSelector} from 'react-redux';

import * as requestParamTypes from '../../../../consts/app/requestParamTypes';
import dataCollectorPollingTypes from '../../../../consts/dataCollector/dataCollectorPollingTypes.ts';
import dataCollectorSources from '../../../../consts/dataCollector/dataCollectorSources.ts';
import dataCollectorStatuses from '../../../../consts/dataCollector/dataCollectorStatuses.ts';
import dataCollectorWorkModes from '../../../../consts/dataCollector/dataCollectorWorkModes.ts';
import * as formInputNames from '../../../../consts/form/formInputNames';
import * as formItemTypes from '../../../../consts/form/formItemTypes';
import * as localizationKeys from '../../../../consts/localization/localizationKeys';
import * as modalTypes from '../../../../consts/modals/modalTypes';
import buildingsService from '../../../../services/buildings/buildingsService';
import gatewaysService from '../../../../services/gateways/gatewaysService';
import {getLocalizedStrings} from '../../../../services/localization/localizationService';
import organizationsService from '../../../../services/organizations/organizationsService';
import settingsService from '../../../../services/settings/settingsService';
import {makeSelectBuildingProfile, makeSelectBuildingsData} from '../../../../state/selectors/buildings';
import {makeSelectCurrentDataCollector, makeSelectDataCollectorTypes} from '../../../../state/selectors/dataCollectors';
import {makeSelectGatewaysData} from '../../../../state/selectors/gateways';
import {makeSelectCities, makeSelectOrganizationsNames} from '../../../../state/selectors/organizations';
import {makeSelectDataCollectorModels} from '../../../../state/selectors/settings';
import {getState} from '../../../../state/store';
import regexUtils from '../../../../utils/regexUtils.js';

const makeSelectCustomGatewaysList = () => (state) => {
    const data = makeSelectGatewaysData()(state);

    return data?.paginatedData?.items || [];
};

const getFormContent = (modalType, form) => {
    const localizedStrings = getLocalizedStrings();
    const currentDataCollector = useSelector(makeSelectCurrentDataCollector());
    const dataCollectorModels = useSelector(makeSelectDataCollectorTypes()) || [];

    const {
        protocolSettings,
        smartSettings,
        typeId,
        buildingID,
        buildingAddress,
        isDataTransferAllowed,
        cityID,
        city,
        organizationID,
        organization,
        additionalCompaniesIds,
        uploadIntervalHour,
        pollingIntervalMinutes,
        pollingType,
        pollingSchedule,
        workMode,
        gatewayEui,
        ipAddress,
    } = currentDataCollector || {};

    const isEdit = modalType === modalTypes.EDIT_DATA_COLLECTOR_MODAL;
    const isAddToBuilding = modalType === modalTypes.ADD_DATA_COLLECTOR_TO_BUILDING_MODAL;

    const title = isEdit
        ? localizedStrings[localizationKeys.DC_PAGE_FORM_TITLE_EDIT]
        : localizedStrings[localizationKeys.DC_PAGE_FORM_TITLE_CREATE];
    const submitButton = isEdit
        ? localizedStrings[localizationKeys.DC_PAGE_EDIT_FORM_SUBMIT_TEXT]
        : localizedStrings[localizationKeys.DC_PAGE_CREATE_FORM_SUBMIT_TEXT];
    const cancelButton = localizedStrings[localizationKeys.ORGS_PAGE_MODAL_FORM_CANCEL_BTN_TEXT];

    let initialValues = {
        [formInputNames.WORK_MODE]: 1,
        [formInputNames.USE_DEFAULT_UPLOAD_INTERVAL]: 'true',
        [formInputNames.SOURCE]: 1,
        [formInputNames.POLLING_TYPE]: dataCollectorPollingTypes.Interval,
    };

    switch (modalType) {
        case modalTypes.COPY_DATA_COLLECTOR_MODAL:
        case modalTypes.EDIT_DATA_COLLECTOR_MODAL: {
            const settings = smartSettings || protocolSettings || {};

            initialValues = {
                ...currentDataCollector,
                ...settings,
                [formInputNames.WORK_MODE]: workMode ?? 1,
                [formInputNames.IP_ADDRESS]:
                    +workMode === dataCollectorWorkModes.Server ? settings.server || ipAddress : ipAddress,
                [formInputNames.SERVER]: +workMode === dataCollectorWorkModes.Server ? undefined : settings.server,
                [formInputNames.SOURCE]:
                    +workMode === dataCollectorWorkModes.Server
                        ? 4
                        : settings.source != null
                        ? Number(settings.source)
                        : 1,
                isDataTransferAllowed: isDataTransferAllowed?.toString(),
                useDefaultUploadInterval:
                    uploadIntervalHour === null || uploadIntervalHour === undefined ? 'true' : 'false',
                uploadIntervalHour,
                pollingIntervalMinutes: pollingIntervalMinutes?.toString(),
                [formInputNames.POLLING_TYPE]: pollingType ?? dataCollectorPollingTypes.Interval,
                [formInputNames.POLLING_SCHEDULE]: pollingSchedule || [],
                [formInputNames.TCP_PORT]:
                    +workMode === dataCollectorWorkModes.Server ? settings.port?.toString() : undefined,
                typeId: {
                    key: typeId,
                    value: dataCollectorModels.find((model) => +model?.key === +typeId)?.value,
                },
                buildingId: {
                    key: buildingID,
                    value: buildingAddress,
                },
                city: {
                    key: cityID,
                    value: city,
                },
                organization: {
                    key: organizationID,
                    value: organization,
                },
                organization2: {
                    key: additionalCompaniesIds?.[0]?.companyId || 1,
                    value: additionalCompaniesIds?.[0]?.companyName || 'Не має даних',
                },
                organization3: {
                    key: additionalCompaniesIds?.[0]?.companyId || 1,
                    value: additionalCompaniesIds?.[1]?.companyName || 'Не має даних',
                },
                organization4: {
                    key: 3,
                    value: additionalCompaniesIds?.[2]?.companyName || 'Не має даних',
                },
                format: {
                    key: 1,
                    value: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_PLACEHOLDER_FORMAT],
                },
                [formInputNames.GATEWAY_EUI]: gatewayEui
                    ? {
                          key: gatewayEui,
                          value: gatewayEui,
                      }
                    : undefined,
            };
            break;
        }

        case modalTypes.ADD_DATA_COLLECTOR_TO_BUILDING_MODAL: {
            const buildingProfile = makeSelectBuildingProfile()(getState());

            initialValues = {
                [formInputNames.WORK_MODE]: 1,
                [formInputNames.USE_DEFAULT_UPLOAD_INTERVAL]: 'true',
                [formInputNames.SOURCE]: 1,
                buildingId: {
                    key: buildingProfile.buildingID,
                    value: buildingProfile.address,
                },
                organization: {
                    key: buildingProfile.organizationID,
                    value: buildingProfile.organizationName,
                },
            };
            break;
        }
    }

    const isLoraWanUspd = (workMode) => Number(workMode) === dataCollectorWorkModes.Gateway;
    const isServerUspd = (workMode) => Number(workMode) === dataCollectorWorkModes.Server;
    const isUploadIntervalEnabled = (formData) =>
        !isLoraWanUspd(formData?.[formInputNames.WORK_MODE]) && !isServerUspd(formData?.[formInputNames.WORK_MODE]);
    const isUploadIntervalHoursEnabled = (formData) =>
        isLoraWanUspd(formData?.[formInputNames.WORK_MODE]) ||
        (!isServerUspd(formData?.[formInputNames.WORK_MODE]) &&
            formData?.[formInputNames.USE_DEFAULT_UPLOAD_INTERVAL] === 'false');

    const isFtpAddressEnabled = (formData) =>
        !isLoraWanUspd(formData?.[formInputNames.WORK_MODE]) &&
        !isServerUspd(formData?.[formInputNames.WORK_MODE]) &&
        Number(formData?.[formInputNames.SOURCE]) !== dataCollectorSources.HTTP;

    const formFields = [
        {
            key: formInputNames.NAME,
            required: true,
            name: formInputNames.NAME,
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_NAME],
            placeholder: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_PLACEHOLDER_NAME],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_REQUIRED_ERROR],
        },
        {
            key: formInputNames.DEVICE_NUMBER,
            required: true,
            name: formInputNames.DEVICE_NUMBER,
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_NUMBER],
            placeholder: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_PLACEHOLDER_NUMBER],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_REQUIRED_ERROR],
        },
        {
            key: formInputNames.SIM_NUMBER,
            name: formInputNames.SIM_NUMBER,
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_SIM_NUMBER],
            placeholder: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_PLACEHOLDER_SIM_NUMBER],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_PLACEHOLDER_SIM_NUMBER_ERROR],
            pattern: regexUtils.PHONE_REGEX_PLUS,
        },
        {
            key: formInputNames.IP_ADDRESS,
            name: formInputNames.IP_ADDRESS,
            required: (formData) => isServerUspd(formData?.[formInputNames.WORK_MODE]),
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_IP_ADDRESS],
            placeholder: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_PLACEHOLDER_IP_ADDRESS],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_REQUIRED_ERROR],
        },
        {
            key: formInputNames.TCP_PORT,
            name: formInputNames.TCP_PORT,
            itemDeps: [formInputNames.WORK_MODE],
            hidden: (formData) => !isServerUspd(formData?.[formInputNames.WORK_MODE]),
            required: (formData) => isServerUspd(formData?.[formInputNames.WORK_MODE]),
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_TCP_PORT],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_REQUIRED_ERROR],
            type: 'number',
            min: '1',
            max: '65535',
            step: '1',
            pattern: regexUtils.PORT_REGEX,
        },
        {
            key: formInputNames.WORK_MODE,
            name: formInputNames.WORK_MODE,
            formItemType: formItemTypes.RADIO,
            required: true,
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_WORK_MODE],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_RADIO_REQUIRED_ERROR],
            radioButtonOptions: [
                {
                    name: localizedStrings[localizationKeys.DC_PAGE_FORM_WORK_MODE_CLIENT],
                    value: 1,
                },
                {
                    name: localizedStrings[localizationKeys.DC_PAGE_FORM_WORK_MODE_SERVER],
                    value: 2,
                },
                {
                    name: localizedStrings[localizationKeys.DC_PAGE_FORM_WORK_MODE_GATEWAY],
                    value: 3,
                },
            ],
        },
        {
            key: formInputNames.GATEWAY_EUI,
            name: formInputNames.GATEWAY_EUI,
            formItemType: formItemTypes.SELECT,
            itemDeps: [formInputNames.WORK_MODE],
            hidden: (formData) => !isLoraWanUspd(formData?.[formInputNames.WORK_MODE]),
            required: (formData) => isLoraWanUspd(formData?.[formInputNames.WORK_MODE]),
            fieldLabel: 'name',
            fieldValue: 'key',
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_GATEWAY],
            placeholder: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_PLACEHOLDER_GATEWAY],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_REQUIRED_ERROR],
            withSearch: true,
            requestOptions: {
                selectRequestData: makeSelectCustomGatewaysList,
                getRequestData: () => gatewaysService.getGatewaysList({page: 1, size: 1000, direction: 1}),
            },
        },
        {
            key: formInputNames.DC_TYPE_ID,
            required: true,
            formItemType: formItemTypes.SELECT,
            name: formInputNames.DC_TYPE_ID,
            fieldLabel: 'value',
            fieldValue: 'value',
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_DC_TYPE],
            placeholder: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_PLACEHOLDER_DC_TYPE],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_REQUIRED_ERROR],
            requestOptions: {
                selectRequestData: makeSelectDataCollectorModels,
                getRequestData: () => settingsService.getDataCollectorModels(),
            },
        },
        {
            key: formInputNames.USE_DEFAULT_UPLOAD_INTERVAL,
            name: formInputNames.USE_DEFAULT_UPLOAD_INTERVAL,
            itemDeps: [formInputNames.WORK_MODE],
            formItemType: formItemTypes.RADIO,
            required: (formData) => isUploadIntervalEnabled(formData),
            hidden: (formData) =>
                isLoraWanUspd(formData?.[formInputNames.WORK_MODE]) ||
                isServerUspd(formData?.[formInputNames.WORK_MODE]),
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_UPLOAD_INTERVAL],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_RADIO_REQUIRED_ERROR],
            radioButtonOptions: [
                {
                    name: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_VALUE_UPLOAD_INTERVAL_DEFAULT],
                    value: 'true',
                },
                {
                    name: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_VALUE_UPLOAD_INTERVAL_CUSTOM],
                    value: 'false',
                },
            ],
        },
        {
            key: formInputNames.UPLOAD_INTERVAL_HOUR,
            name: formInputNames.UPLOAD_INTERVAL_HOUR,
            itemDeps: [formInputNames.WORK_MODE, formInputNames.USE_DEFAULT_UPLOAD_INTERVAL],
            hidden: (formData) => !isUploadIntervalHoursEnabled(formData),
            required: (formData) => isUploadIntervalHoursEnabled(formData),
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_UPLOAD_INTERVAL_HOURS],
            placeholder: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_PLACEHOLDER_UPLOAD_INTERVAL_HOURS],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_REQUIRED_ERROR],
            type: 'number',
            min: '1',
            max: '24',
            onInput: (e) => {
                const value = parseInt(e.target.value, 10);

                if (value < 1) e.target.value = 1;
                if (value > 24) e.target.value = 24;
            },
        },
        {
            key: formInputNames.CITY,
            formItemType: formItemTypes.SELECT,
            required: !isAddToBuilding,
            fieldLabel: 'name',
            name: formInputNames.CITY,
            hidden: isAddToBuilding,
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_CITY],
            placeholder: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_PLACEHOLDER_CITY],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_REQUIRED_ERROR],
            requestOptions: {
                selectRequestData: makeSelectCities,
                getRequestData: () => organizationsService.getCities(),
            },
        },
        {
            key: formInputNames.ORGANIZATION,
            formItemType: formItemTypes.SELECT,
            required: true,
            fieldLabel: 'value',
            fieldValue: 'value',
            name: formInputNames.ORGANIZATION,
            hidden: isAddToBuilding,
            itemDeps: formInputNames.CITY,
            disabled: (_, cityId) => !cityId || isAddToBuilding,
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_COMPANY],
            placeholder: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_PLACEHOLDER_COMPANY],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_REQUIRED_ERROR],
            requestOptions: {
                selectRequestData: makeSelectOrganizationsNames,
                getRequestData: () => organizationsService.getOrganizationsNames(),
            },
        },
        {
            key: formInputNames.BUILDING_ID,
            required: true,
            name: formInputNames.BUILDING_ID,
            itemDeps: formInputNames.ORGANIZATION,
            hidden: isAddToBuilding,
            disabled: (_, organizationID) => !organizationID || isAddToBuilding,
            fieldLabel: 'value',
            fieldValue: 'value',
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_BUILDING],
            withSearch: true,
            placeholder: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_PLACEHOLDER_BUILDING],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_REQUIRED_ERROR],
            formItemType: formItemTypes.SELECT,
            requestOptions: {
                paramsType: requestParamTypes.BUILDINGS,
                selectRequestData: makeSelectBuildingsData,
                getRequestData: () => {
                    const currentOrg = form?.getFieldValue(formInputNames.ORGANIZATION);
                    const activeOrgId = currentOrg?.key || currentOrg || organizationID;

                    return buildingsService.getBuildingsListByOrganizationId(activeOrgId);
                },
            },
        },
        {
            key: formInputNames.STATUS,
            required: false,
            hidden: !isEdit,
            formItemType: formItemTypes.RADIO,
            name: formInputNames.STATUS,
            label: localizedStrings[localizationKeys.DC_PAGE_STATUS],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_RADIO_REQUIRED_ERROR],
            radioButtonOptions: [
                {
                    name: localizedStrings[localizationKeys.DC_PAGE_STATUS_ACTIVE],
                    value: dataCollectorStatuses.Active,
                },
                {
                    name: localizedStrings[localizationKeys.DC_PAGE_STATUS_BLOCKED],
                    value: dataCollectorStatuses.Blocked,
                },
            ],
        },
        {
            key: formInputNames.IS_DATA_TRANSFER_ALLOWED,
            required: true,
            name: formInputNames.IS_DATA_TRANSFER_ALLOWED,
            formItemType: formItemTypes.RADIO,
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_DATA_TRANSFER],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_RADIO_REQUIRED_ERROR],
            radioButtonOptions: [
                {
                    name: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_VALUE_DATA_TRANSFER_ENABLED],
                    value: 'true',
                },
                {
                    name: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_VALUE_DATA_TRANSFER_DISABLED],
                    value: 'false',
                },
            ],
        },
        {
            key: `${formInputNames.HANDLER}_client`,
            name: formInputNames.HANDLER,
            formItemType: formItemTypes.SELECT,
            fieldLabel: 'name',
            fieldValue: 'value',
            itemDeps: [formInputNames.WORK_MODE],
            hidden: (formData) =>
                isLoraWanUspd(formData?.[formInputNames.WORK_MODE]) ||
                isServerUspd(formData?.[formInputNames.WORK_MODE]),
            required: (formData) =>
                !isLoraWanUspd(formData?.[formInputNames.WORK_MODE]) &&
                !isServerUspd(formData?.[formInputNames.WORK_MODE]),
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_HANDLER_FILE_TYPE],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_REQUIRED_ERROR],
            placeholder: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_PLACEHOLDER_CHOOSE_TYPE],
            requestOptions: {
                options: [
                    {
                        name: localizedStrings[localizationKeys.DC_PAGE_HANDLER_TYPE_SMART_FILE],
                        value: 1,
                    },
                    {
                        name: localizedStrings[localizationKeys.DC_PAGE_HANDLER_TYPE_PROTOCOL_FILE],
                        value: 2,
                    },
                    {
                        name: localizedStrings[localizationKeys.DC_PAGE_HANDLER_TYPE_JSON_FILE],
                        value: 3,
                    },
                    {
                        name: localizedStrings[localizationKeys.DC_PAGE_HANDLER_TYPE_MBUS_FILE],
                        value: 4,
                    },
                    {
                        name: localizedStrings[localizationKeys.DC_PAGE_HANDLER_TYPE_MODBUS_FILE],
                        value: 5,
                    },
                ],
            },
        },
        {
            key: `${formInputNames.HANDLER}_server`,
            name: formInputNames.HANDLER,
            formItemType: formItemTypes.SELECT,
            fieldLabel: 'name',
            fieldValue: 'value',
            itemDeps: [formInputNames.WORK_MODE],
            hidden: (formData) => !isServerUspd(formData?.[formInputNames.WORK_MODE]),
            required: (formData) => isServerUspd(formData?.[formInputNames.WORK_MODE]),
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_HANDLER_PROTOCOL_TYPE],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_REQUIRED_ERROR],
            placeholder: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_PLACEHOLDER_CHOOSE_TYPE],
            requestOptions: {
                options: [
                    {
                        name: localizedStrings[localizationKeys.DC_PAGE_PROTOCOL_TYPE_MBUS],
                        value: 4,
                    },
                    {
                        name: localizedStrings[localizationKeys.DC_PAGE_PROTOCOL_TYPE_MODBUS],
                        value: 5,
                    },
                    {
                        name: localizedStrings[localizationKeys.DC_PAGE_PROTOCOL_TYPE_SVTU11RP],
                        value: 6,
                    },
                ],
            },
        },
        {
            key: formInputNames.SOURCE,
            name: formInputNames.SOURCE,
            formItemType: formItemTypes.RADIO,
            itemDeps: [formInputNames.WORK_MODE],
            hidden: (formData) =>
                isLoraWanUspd(formData?.[formInputNames.WORK_MODE]) ||
                isServerUspd(formData?.[formInputNames.WORK_MODE]),
            required: (formData) =>
                !isLoraWanUspd(formData?.[formInputNames.WORK_MODE]) &&
                !isServerUspd(formData?.[formInputNames.WORK_MODE]),
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_PROTOCOL],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_RADIO_REQUIRED_ERROR],
            radioButtonOptions: [
                {
                    name: localizedStrings[localizationKeys.DC_PAGE_FORM_PROTOCOL_FTP],
                    value: 1,
                },
                {
                    name: localizedStrings[localizationKeys.DC_PAGE_FORM_PROTOCOL_EMAIL],
                    value: 2,
                },
                {
                    name: localizedStrings[localizationKeys.DC_PAGE_FORM_PROTOCOL_HTTP],
                    value: 3,
                },
            ],
        },
        {
            key: formInputNames.SERVER,
            name: formInputNames.SERVER,
            itemDeps: [formInputNames.WORK_MODE, formInputNames.SOURCE],
            hidden: (formData) =>
                isLoraWanUspd(formData?.[formInputNames.WORK_MODE]) ||
                isServerUspd(formData?.[formInputNames.WORK_MODE]) ||
                Number(formData?.[formInputNames.SOURCE]) === dataCollectorSources.HTTP,
            required: (formData) => isFtpAddressEnabled(formData),
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_FTP_ADDRESS],
            placeholder: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_PLACEHOLDER_FTP_ADDRESS],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_REQUIRED_ERROR],
        },
        {
            key: formInputNames.FOLDER,
            name: formInputNames.FOLDER,
            itemDeps: [formInputNames.WORK_MODE, formInputNames.SOURCE],
            hidden: (formData) =>
                isLoraWanUspd(formData?.[formInputNames.WORK_MODE]) ||
                isServerUspd(formData?.[formInputNames.WORK_MODE]) ||
                Number(formData?.[formInputNames.SOURCE]) === dataCollectorSources.HTTP,
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_FOLDER_PATH],
            placeholder: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_PLACEHOLDER_FOLDER_PATH],
        },
        {
            key: formInputNames.LOGIN,
            name: formInputNames.LOGIN,
            itemDeps: [formInputNames.WORK_MODE, formInputNames.SOURCE],
            hidden: (formData) =>
                isLoraWanUspd(formData?.[formInputNames.WORK_MODE]) ||
                isServerUspd(formData?.[formInputNames.WORK_MODE]) ||
                Number(formData?.[formInputNames.SOURCE]) === dataCollectorSources.HTTP,
            required: (formData) => isFtpAddressEnabled(formData),
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_LOGIN],
            placeholder: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_PLACEHOLDER_LOGIN],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_REQUIRED_ERROR],
        },
        {
            key: formInputNames.PASSWORD,
            formItemType: formItemTypes.PASSWORD,
            name: formInputNames.PASSWORD,
            itemDeps: [formInputNames.WORK_MODE, formInputNames.SOURCE],
            hidden: (formData) =>
                isLoraWanUspd(formData?.[formInputNames.WORK_MODE]) ||
                isServerUspd(formData?.[formInputNames.WORK_MODE]) ||
                Number(formData?.[formInputNames.SOURCE]) === dataCollectorSources.HTTP,
            required: (formData) => isFtpAddressEnabled(formData),
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_PASSWORD],
            placeholder: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_PLACEHOLDER_PASSWORD],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_REQUIRED_ERROR],
        },
        {
            key: formInputNames.POLLING_TYPE,
            name: formInputNames.POLLING_TYPE,
            formItemType: formItemTypes.RADIO,
            itemDeps: [formInputNames.WORK_MODE],
            hidden: (formData) => Number(formData?.[formInputNames.WORK_MODE]) !== dataCollectorWorkModes.Server,
            required: (formData) => Number(formData?.[formInputNames.WORK_MODE]) === dataCollectorWorkModes.Server,
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_POLLING_TYPE],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_RADIO_REQUIRED_ERROR],
            radioButtonOptions: [
                {
                    name: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_VALUE_POLLING_INTERVAL],
                    value: dataCollectorPollingTypes.Interval,
                },
                {
                    name: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_VALUE_POLLING_SCHEDULE],
                    value: dataCollectorPollingTypes.Schedule,
                },
            ],
        },
        {
            key: formInputNames.POLLING_INTERVAL_MINUTES,
            name: formInputNames.POLLING_INTERVAL_MINUTES,
            itemDeps: [formInputNames.WORK_MODE, formInputNames.POLLING_TYPE],
            hidden: (formData) =>
                Number(formData?.[formInputNames.WORK_MODE]) !== dataCollectorWorkModes.Server ||
                Number(formData?.[formInputNames.POLLING_TYPE]) !== dataCollectorPollingTypes.Interval,
            required: (formData) =>
                Number(formData?.[formInputNames.WORK_MODE]) === dataCollectorWorkModes.Server &&
                Number(formData?.[formInputNames.POLLING_TYPE]) === dataCollectorPollingTypes.Interval,
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_POLLING_INTERVAL_MINUTES],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_REQUIRED_ERROR],
            type: 'number',
            min: '1',
            step: '1',
            pattern: /^[1-9]\d*$/,
        },
        {
            key: formInputNames.POLLING_SCHEDULE,
            name: formInputNames.POLLING_SCHEDULE,
            formItemType: formItemTypes.POLLING_SCHEDULE,
            itemDeps: [formInputNames.WORK_MODE, formInputNames.POLLING_TYPE],
            row: true,
            hidden: (formData) =>
                Number(formData?.[formInputNames.WORK_MODE]) !== dataCollectorWorkModes.Server ||
                Number(formData?.[formInputNames.POLLING_TYPE]) !== dataCollectorPollingTypes.Schedule,
            required: (formData) =>
                Number(formData?.[formInputNames.WORK_MODE]) === dataCollectorWorkModes.Server &&
                Number(formData?.[formInputNames.POLLING_TYPE]) === dataCollectorPollingTypes.Schedule,
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_POLLING_SCHEDULE],
            errorMessage: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_POLLING_SCHEDULE_ERROR],
            addLabel: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_POLLING_SCHEDULE_ADD],
            addDayLabel: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_POLLING_SCHEDULE_ADD_DAY],
            removeLabel: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_POLLING_SCHEDULE_REMOVE],
            removeDayLabel: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_POLLING_SCHEDULE_REMOVE_DAY],
            timePlaceholder: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_POLLING_SCHEDULE_TIME],
            dayOptions: [
                {value: 1, label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_WEEKDAY_MONDAY]},
                {value: 2, label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_WEEKDAY_TUESDAY]},
                {value: 3, label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_WEEKDAY_WEDNESDAY]},
                {value: 4, label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_WEEKDAY_THURSDAY]},
                {value: 5, label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_WEEKDAY_FRIDAY]},
                {value: 6, label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_WEEKDAY_SATURDAY]},
                {value: 7, label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_WEEKDAY_SUNDAY]},
            ],
            validator: (entries) => {
                if (!Array.isArray(entries) || entries.length === 0) return false;

                const validEntries = entries.filter(
                    (entry) =>
                        Number.isInteger(entry?.dayOfWeek) &&
                        entry.dayOfWeek >= 1 &&
                        entry.dayOfWeek <= 7 &&
                        Number.isInteger(entry?.hour) &&
                        entry.hour >= 0 &&
                        entry.hour <= 23 &&
                        Number.isInteger(entry?.minute) &&
                        entry.minute >= 0 &&
                        entry.minute <= 59
                );
                const uniqueEntries = new Set(
                    validEntries.map((entry) => `${entry.dayOfWeek}-${entry.hour}-${entry.minute}`)
                );

                return validEntries.length === entries.length && uniqueEntries.size === entries.length;
            },
        },
        {
            key: formInputNames.FORMAT,
            name: formInputNames.FORMAT,
            formItemType: formItemTypes.SELECT,
            fieldLabel: 'value',
            fieldValue: 'key',
            hidden: true,
            label: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_LABEL_FORMAT],
            disabled: true,
            placeholder: localizedStrings[localizationKeys.DC_PAGE_FORM_FIELD_PLACEHOLDER_FORMAT],
        },
    ];

    return {formFields, title, submitButton, cancelButton, initialValues};
};

export default {
    getFormContent,
};
